import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Layout, Users, Star, Eye, FileText, Palette, Settings as SettingsIcon, Copy, X, ExternalLink, Link as LinkIcon, Loader2, Package, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

// Predefined module catalog — taken from built-in templates. Admin can also add custom codes.
const MODULE_CATALOG = [
  { group: 'Produk & Checkout', items: [
    { code: 'products_physical', label: 'Produk Fisik' },
    { code: 'products_digital', label: 'Produk Digital' },
    { code: 'checkout', label: 'Checkout' },
    { code: 'payment_gateway', label: 'Payment Gateway' },
    { code: 'cod', label: 'COD' },
    { code: 'promo', label: 'Promo & Voucher' },
  ]},
  { group: 'LMS & Membership', items: [
    { code: 'lms', label: 'LMS / Kursus' },
    { code: 'membership', label: 'Membership' },
    { code: 'subscription', label: 'Subscription' },
    { code: 'certificates', label: 'Sertifikat' },
    { code: 'quiz', label: 'Quiz' },
    { code: 'webinar', label: 'Webinar' },
  ]},
  { group: 'High Ticket / Project', items: [
    { code: 'payment_plan', label: 'Payment Plan' },
    { code: 'booking_fee', label: 'Booking Fee' },
    { code: 'dp_termin', label: 'DP & Termin' },
    { code: 'contracts', label: 'Kontrak' },
    { code: 'milestones', label: 'Milestones' },
    { code: 'invoice_staged', label: 'Invoice Staged' },
    { code: 'follow_up', label: 'Follow-Up' },
    { code: 'project_tracker', label: 'Project Tracker' },
  ]},
  { group: 'Affiliate & Dashboard', items: [
    { code: 'affiliate', label: 'Affiliate' },
    { code: 'affiliate_basic', label: 'Affiliate Basic' },
    { code: 'customer_dashboard', label: 'Customer Dashboard' },
    { code: 'member_dashboard', label: 'Member Dashboard' },
  ]},
];

const PAGE_TYPES = [
  'home', 'catalog', 'categories', 'cart', 'checkout', 'promo', 'products',
  'static', 'contact', 'faq', 'blog',
  'courses', 'membership', 'instructors', 'testimonials', 'member_area',
  'services', 'packages', 'portfolio', 'pricing_calculator',
  'consultation', 'order_form', 'payment_progress', 'custom',
];

const PRESET_COLORS = ['#2D6CDF', '#0F52FF', '#1d4ed8', '#7c3aed', '#059669', '#10b981', '#0d9488', '#dc2626', '#ea580c', '#f59e0b', '#0ea5e9', '#22d3ee', '#ec4899', '#1e293b', '#374151', '#0f172a'];

// Mapping: package feature code → module codes that should auto-tick when feature is included.
// Used to auto-suggest modul checklist when admin picks a SaaS package.
const FEATURE_TO_MODULES = {
  physical: ['products_physical'],
  digital: ['products_digital'],
  serial_key: ['products_digital'],
  cod: ['cod'],
  voucher: ['promo'],
  promo: ['promo'],
  course: ['lms', 'certificates', 'quiz'],
  lms: ['lms', 'certificates', 'quiz'],
  membership: ['membership', 'subscription', 'member_dashboard'],
  memberships: ['membership', 'subscription', 'member_dashboard'],
  subscription: ['subscription'],
  webinar: ['webinar'],
  high_ticket: ['payment_plan', 'booking_fee', 'dp_termin', 'contracts', 'milestones', 'invoice_staged', 'follow_up', 'project_tracker'],
  payment_plan: ['payment_plan'],
  affiliate: ['affiliate', 'affiliate_basic'],
  affiliates: ['affiliate', 'affiliate_basic'],
};

// Modules that should always be ticked regardless of features (storefront essentials).
const ALWAYS_ON_MODULES = ['checkout', 'payment_gateway', 'customer_dashboard'];

// Compute the union of module codes implied by a set of SaaS packages (by their `features` arrays).
function modulesForPackages(selectedPackageIds, allPackages) {
  const features = new Set();
  for (const p of allPackages) {
    if (!selectedPackageIds.includes(p.id)) continue;
    for (const f of (p.features || [])) features.add(f);
  }
  const modules = new Set(ALWAYS_ON_MODULES);
  for (const f of features) {
    for (const m of (FEATURE_TO_MODULES[f] || [])) modules.add(m);
  }
  return Array.from(modules);
}

function emptyForm() {
  return {
    name: '', description: '', category: 'commerce',
    default_modules: [],
    default_pages: [{ slug: 'home', title: 'Beranda', type: 'home' }],
    default_settings: {},
    color_schemes: [{ name: 'Default', primary: '#2D6CDF', secondary: '#1e293b', accent: '#10b981' }],
    available_packages: [],
  };
}

export default function AdminStoreTemplates() {
  const [templates, setTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [customModuleCode, setCustomModuleCode] = useState('');
  // Track modules auto-added by package selection (so admin can tell what was auto-suggested)
  const [autoSuggestedModules, setAutoSuggestedModules] = useState([]);
  // Preview modal state
  const [previewTemplate, setPreviewTemplate] = useState(null);
  // Set demo URL dialog state
  const [demoUrlTarget, setDemoUrlTarget] = useState(null); // template object
  const [demoUrlInput, setDemoUrlInput] = useState('');
  const [savingDemoUrl, setSavingDemoUrl] = useState(false);
  // Set package availability dialog state
  const [availabilityTarget, setAvailabilityTarget] = useState(null);
  const [availabilityInput, setAvailabilityInput] = useState([]);
  const [savingAvailability, setSavingAvailability] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const [tplRes, pkgRes] = await Promise.all([
        api.get('/admin/store-templates'),
        api.get('/admin/packages').catch(() => ({ data: { packages: [] } })),
      ]);
      const all = tplRes.data.templates || [];
      setTemplates(all.filter(t => !t.is_custom));
      setCustomTemplates(all.filter(t => t.is_custom));
      setPackages(pkgRes.data.packages || pkgRes.data || []);
    } catch { toast.error('Gagal memuat template'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  // When the admin toggles a package in the multi-select, recompute auto-suggested modules
  // and merge them into the form's default_modules (without removing what the admin has
  // explicitly ticked). This is a hint/shortcut — admin can still uncheck manually.
  const togglePackageAvailability = (pkgId) => {
    setForm((p) => {
      const has = (p.available_packages || []).includes(pkgId);
      const nextPkgs = has
        ? p.available_packages.filter((x) => x !== pkgId)
        : [...(p.available_packages || []), pkgId];
      const suggested = modulesForPackages(nextPkgs, packages);
      // Merge: keep existing modules + add newly suggested ones (no removal)
      const merged = Array.from(new Set([...(p.default_modules || []), ...suggested]));
      setAutoSuggestedModules(suggested);
      return { ...p, available_packages: nextPkgs, default_modules: merged };
    });
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openCreate = () => {
    setForm(emptyForm());
    setEditTarget(null);
    setCustomModuleCode('');
    setAutoSuggestedModules([]);
    setShowCreate(true);
  };

  const openEdit = (t) => {
    setEditTarget(t.template_id);
    setForm({
      name: t.name || '',
      description: t.description || '',
      category: t.category || 'commerce',
      default_modules: Array.isArray(t.default_modules) ? [...t.default_modules] : [],
      default_pages: Array.isArray(t.default_pages) && t.default_pages.length
        ? t.default_pages.map((p) => ({ slug: p.slug || '', title: p.title || '', type: p.type || 'static' }))
        : [{ slug: 'home', title: 'Beranda', type: 'home' }],
      default_settings: t.default_settings || {},
      color_schemes: Array.isArray(t.color_schemes) && t.color_schemes.length
        ? t.color_schemes.map((c) => ({ name: c.name || '', primary: c.primary || '#2D6CDF', secondary: c.secondary || '#1e293b', accent: c.accent || '#10b981' }))
        : [{ name: 'Default', primary: '#2D6CDF', secondary: '#1e293b', accent: '#10b981' }],
      available_packages: Array.isArray(t.available_packages) ? [...t.available_packages] : [],
    });
    setCustomModuleCode('');
    setAutoSuggestedModules([]);
    setShowCreate(true);
  };

  // Pre-fill form from a built-in template (clone). Keeps name & description blank
  // so admin can give the new template a unique identity.
  const cloneFromBuiltIn = (templateId) => {
    if (!templateId) return;
    const src = templates.find((t) => (t.template_id || t.id) === templateId);
    if (!src) return;
    setForm({
      name: '',
      description: '',
      category: src.category || 'commerce',
      default_modules: Array.isArray(src.default_modules) ? [...src.default_modules] : [],
      default_pages: Array.isArray(src.default_pages)
        ? src.default_pages.map((p) => ({ slug: p.slug || '', title: p.title || '', type: p.type || 'static' }))
        : [],
      default_settings: { ...(src.default_settings || {}) },
      color_schemes: Array.isArray(src.color_schemes)
        ? src.color_schemes.map((c) => ({ name: c.name || '', primary: c.primary || '', secondary: c.secondary || '', accent: c.accent || '' }))
        : [],
      available_packages: Array.isArray(src.available_packages) ? [...src.available_packages] : [],
    });
    toast.success(`Disalin dari template "${src.name}". Atur nama & deskripsi yang baru.`);
  };

  const toggleModule = (code) => {
    setForm((p) => {
      const has = p.default_modules.includes(code);
      return { ...p, default_modules: has ? p.default_modules.filter((c) => c !== code) : [...p.default_modules, code] };
    });
  };

  const addCustomModule = () => {
    const code = customModuleCode.trim().toLowerCase().replace(/\s+/g, '_');
    if (!code) return;
    if (form.default_modules.includes(code)) {
      toast.error('Modul sudah ada');
      return;
    }
    setForm((p) => ({ ...p, default_modules: [...p.default_modules, code] }));
    setCustomModuleCode('');
  };

  const addPage = () => {
    setForm((p) => ({ ...p, default_pages: [...p.default_pages, { slug: '', title: '', type: 'static' }] }));
  };
  const updatePage = (idx, key, value) => {
    setForm((p) => ({
      ...p,
      default_pages: p.default_pages.map((pg, i) => (i === idx ? { ...pg, [key]: value } : pg)),
    }));
  };
  const removePage = (idx) => {
    setForm((p) => ({ ...p, default_pages: p.default_pages.filter((_, i) => i !== idx) }));
  };

  const addColorScheme = () => {
    setForm((p) => ({
      ...p,
      color_schemes: [...p.color_schemes, { name: `Scheme ${p.color_schemes.length + 1}`, primary: '#2D6CDF', secondary: '#1e293b', accent: '#10b981' }],
    }));
  };
  const updateColorScheme = (idx, key, value) => {
    setForm((p) => ({
      ...p,
      color_schemes: p.color_schemes.map((cs, i) => (i === idx ? { ...cs, [key]: value } : cs)),
    }));
  };
  const removeColorScheme = (idx) => {
    setForm((p) => ({ ...p, color_schemes: p.color_schemes.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.description.trim()) {
      return toast.error('Nama dan deskripsi wajib diisi');
    }
    if ((form.color_schemes || []).length === 0) {
      return toast.error('Minimal 1 skema warna diperlukan');
    }
    // Drop empty pages (no slug + no title)
    const cleanPages = (form.default_pages || []).filter((p) => p.slug.trim() || p.title.trim()).map((p) => ({
      slug: (p.slug || '').trim().toLowerCase(), title: (p.title || '').trim(), type: (p.type || 'static').trim(),
    }));
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      category: form.category,
      default_modules: form.default_modules,
      default_pages: cleanPages,
      default_settings: form.default_settings || {},
      color_schemes: form.color_schemes.map((c) => ({
        name: (c.name || '').trim() || 'Default', primary: c.primary, secondary: c.secondary, accent: c.accent,
      })),
      is_active: true,
      available_packages: form.available_packages || [],
    };
    setSaving(true);
    try {
      if (editTarget) {
        await api.put(`/admin/store-templates/${editTarget}`, payload);
        toast.success('Template diupdate');
      } else {
        await api.post('/admin/store-templates', payload);
        toast.success('Template dibuat');
      }
      setShowCreate(false);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const openDemoUrlDialog = (t) => {
    setDemoUrlTarget(t);
    setDemoUrlInput(t.demo_url || '');
  };

  const saveDemoUrl = async () => {
    if (!demoUrlTarget) return;
    setSavingDemoUrl(true);
    try {
      await api.put(`/admin/store-templates/${demoUrlTarget.template_id}/demo-url`, {
        demo_url: demoUrlInput.trim(),
      });
      toast.success(demoUrlInput.trim() ? 'Demo URL disimpan' : 'Demo URL dihapus');
      setDemoUrlTarget(null);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan demo URL');
    } finally {
      setSavingDemoUrl(false);
    }
  };

  // Preview strategy:
  // - If admin set an explicit demo_url, honor it (real seeded store for built-ins).
  // - Otherwise open the self-contained mockup renderer, which works for ANY template
  //   (custom templates have no live demo store, so this is their reliable preview).
  const openLivePreview = (t) => {
    const id = t.template_id || t.id;
    if (t.demo_url) {
      window.open(t.demo_url, '_blank', 'noopener,noreferrer');
    } else {
      window.open(`/template-preview/${id}`, '_blank', 'noopener,noreferrer');
    }
  };

  // Always-available mockup preview, regardless of demo_url. Used by custom templates
  // so a wrong/broken demo_url never leaves the user without a working preview.
  const openMockupPreview = (t) => {
    const id = t.template_id || t.id;
    window.open(`/template-preview/${id}`, '_blank', 'noopener,noreferrer');
  };

  const openAvailabilityDialog = (t) => {
    setAvailabilityTarget(t);
    setAvailabilityInput(Array.isArray(t.available_packages) ? [...t.available_packages] : []);
  };

  const toggleAvailabilityPkg = (pkgId) => {
    setAvailabilityInput((arr) => arr.includes(pkgId) ? arr.filter((x) => x !== pkgId) : [...arr, pkgId]);
  };

  const saveAvailability = async () => {
    if (!availabilityTarget) return;
    setSavingAvailability(true);
    try {
      await api.put(`/admin/store-templates/${availabilityTarget.template_id}/availability`, {
        available_packages: availabilityInput,
      });
      toast.success(availabilityInput.length
        ? `Tersedia untuk ${availabilityInput.length} paket`
        : 'Tersedia untuk semua paket');
      setAvailabilityTarget(null);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSavingAvailability(false);
    }
  };

  const packageNamesFor = (pkgIds) => {
    if (!Array.isArray(pkgIds) || pkgIds.length === 0) return 'Semua paket';
    const names = pkgIds.map((id) => {
      const p = packages.find((x) => x.id === id);
      return p ? p.name : null;
    }).filter(Boolean);
    return names.length ? names.join(', ') : `${pkgIds.length} paket`;
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Hapus template ini?')) return;
    try {
      await api.delete(`/admin/store-templates/${templateId}`);
      toast.success('Template dihapus');
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menghapus'); }
  };

  const categoryColor = (c) => {
    if (c === 'commerce') return 'bg-blue-100 text-blue-700';
    if (c === 'education') return 'bg-violet-100 text-violet-700';
    if (c === 'services') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-700';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Template Toko</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola template toko yang tersedia untuk tenant</p>
        </div>
        <Button onClick={openCreate} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
          <Plus size={16} className="mr-2" />Buat Template
        </Button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>
      ) : (
        <div className="space-y-8">
          {/* Built-in Templates */}
          <div>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Template Bawaan</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {templates.map(t => (
                <Card key={t.id} className="border-slate-200 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#2D6CDF]/10 flex items-center justify-center">
                        <Layout size={20} className="text-[#2D6CDF]" />
                      </div>
                      <Badge className={categoryColor(t.category)}>{t.category}</Badge>
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1">{t.name}</h3>
                    <p className="text-sm text-slate-500 mb-4 line-clamp-2">{t.description}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500 border-t border-slate-100 pt-3 mb-2">
                      <div className="flex items-center gap-1">
                        <Users size={13} />
                        <span>{t.usage_count || 0} tenant</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star size={13} />
                        <span>{(t.default_modules || []).length} modul</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FileText size={13} />
                        <span>{(t.default_pages || []).length} halaman</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => openAvailabilityDialog(t)}
                      className="w-full text-[11px] text-left mb-3 px-2 py-1.5 rounded border border-dashed border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-colors group"
                      title="Atur paket SaaS yang boleh memakai template ini"
                    >
                      <span className="flex items-center gap-1 text-slate-500 group-hover:text-emerald-700">
                        <Package size={11} />
                        <span className="font-medium">Paket:</span>
                        <span className="truncate">{packageNamesFor(t.available_packages)}</span>
                      </span>
                    </button>
                    <div className="flex gap-2">
                      {t.demo_url ? (
                        <Button
                          size="sm"
                          className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                          onClick={() => openLivePreview(t)}
                          data-testid={`preview-live-${t.id}`}
                        >
                          <Eye size={13} className="mr-1.5" /> Preview Live
                          <ExternalLink size={11} className="ml-1.5 opacity-80" />
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 border-dashed"
                          onClick={() => openDemoUrlDialog(t)}
                          data-testid={`set-demo-url-${t.id}`}
                        >
                          <LinkIcon size={13} className="mr-1.5" /> Set Demo URL
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => setPreviewTemplate(t)} title="Detail template" data-testid={`preview-template-${t.id}`}>
                        <FileText size={13} />
                      </Button>
                      {t.demo_url && (
                        <Button variant="outline" size="sm" onClick={() => openDemoUrlDialog(t)} title="Edit demo URL">
                          <LinkIcon size={13} />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Custom Templates */}
          <div>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Template Kustom {customTemplates.length > 0 && `(${customTemplates.length})`}
            </h2>
            {customTemplates.length === 0 ? (
              <Card className="border-dashed border-2 border-slate-200">
                <CardContent className="py-12 text-center">
                  <Layout size={32} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Belum ada template kustom</p>
                  <p className="text-xs text-slate-400 mt-1">Buat template khusus sesuai kebutuhan</p>
                  <Button onClick={openCreate} variant="outline" size="sm" className="mt-4">
                    <Plus size={14} className="mr-1.5" />Buat Template Kustom
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {customTemplates.map(t => (
                  <Card key={t.id} className="border-slate-200 shadow-sm">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                          <Layout size={20} className="text-emerald-600" />
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge className={categoryColor(t.category)}>{t.category}</Badge>
                          <Badge variant="outline" className="text-xs">Kustom</Badge>
                        </div>
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-1">{t.name}</h3>
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2">{t.description}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500 border-t border-slate-100 pt-3 mb-2">
                        <div className="flex items-center gap-1">
                          <Users size={13} />
                          <span>{t.usage_count || 0} tenant</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star size={13} />
                          <span>{(t.default_modules || []).length} modul</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FileText size={13} />
                          <span>{(t.default_pages || []).length} halaman</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => openAvailabilityDialog(t)}
                        className="w-full text-[11px] text-left mb-3 px-2 py-1.5 rounded border border-dashed border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-colors group"
                        title="Atur paket SaaS yang boleh memakai template ini"
                      >
                        <span className="flex items-center gap-1 text-slate-500 group-hover:text-emerald-700">
                          <Package size={11} />
                          <span className="font-medium">Paket:</span>
                          <span className="truncate">{packageNamesFor(t.available_packages)}</span>
                        </span>
                      </button>
                      <div className="flex gap-2">
                        {/* Custom templates always get a working mockup preview (no live demo store needed) */}
                        <Button size="sm" className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => openMockupPreview(t)}>
                          <Eye size={13} className="mr-1.5" />Preview Live
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setPreviewTemplate(t)} title="Detail template">
                          <FileText size={13} />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openDemoUrlDialog(t)} title={t.demo_url ? 'Edit demo URL (opsional)' : 'Set demo URL toko asli (opsional)'}>
                          <LinkIcon size={13} className={t.demo_url ? 'text-emerald-600' : ''} />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEdit(t)} title="Edit">
                          <Pencil size={13} />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => handleDelete(t.template_id)} title="Hapus">
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Set Package Availability Dialog */}
      <Dialog open={!!availabilityTarget} onOpenChange={(o) => !o && setAvailabilityTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={18} className="text-emerald-600" /> Paket SaaS untuk Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-slate-700">
              Template: <strong>{availabilityTarget?.name}</strong>
            </div>
            <p className="text-xs text-slate-500">
              Centang paket-paket yang boleh memakai template ini. Tenant yang berlangganan paket terpilih akan melihat template ini saat memilih template toko.
              <br />
              <span className="text-amber-700">Kosongkan semua = template tersedia untuk semua paket.</span>
            </p>
            {packages.length === 0 ? (
              <p className="text-sm text-slate-400 italic py-4 text-center">Belum ada paket SaaS. Buat di menu Paket SaaS.</p>
            ) : (
              <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
                {packages.map((p) => {
                  const checked = availabilityInput.includes(p.id);
                  return (
                    <label
                      key={p.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAvailabilityPkg(p.id)}
                        className="mt-1 accent-emerald-600"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-slate-900">{p.name}</p>
                          {p.price_monthly != null && (
                            <span className="text-xs text-slate-500">Rp {Number(p.price_monthly).toLocaleString('id-ID')}/bln</span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {(p.features || []).length} fitur • {p.max_products === -1 ? 'Unlimited' : p.max_products} produk
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvailabilityTarget(null)} disabled={savingAvailability}>Batal</Button>
            <Button onClick={saveAvailability} disabled={savingAvailability} className="bg-emerald-600 hover:bg-emerald-700">
              {savingAvailability ? <><Loader2 size={14} className="animate-spin mr-2" /> Menyimpan...</> : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Demo URL Dialog */}
      <Dialog open={!!demoUrlTarget} onOpenChange={(o) => !o && setDemoUrlTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon size={18} className="text-emerald-600" /> Demo URL Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-slate-700">
              Template: <strong>{demoUrlTarget?.name}</strong>
            </div>
            <p className="text-xs text-slate-500">
              Masukkan URL toko demo yang menampilkan tampilan template ini secara langsung. Tenant/admin akan klik tombol "Preview Live" untuk membukanya di tab baru.
            </p>
            <div>
              <Label>Demo URL</Label>
              <Input
                value={demoUrlInput}
                onChange={(e) => setDemoUrlInput(e.target.value)}
                placeholder="https://demo.tokomoe.com/store/contoh"
                className="mt-1 font-mono text-xs"
                data-testid="demo-url-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">Kosongkan untuk menghapus demo URL.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDemoUrlTarget(null)} disabled={savingDemoUrl}>Batal</Button>
            <Button onClick={saveDemoUrl} disabled={savingDemoUrl} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]">
              {savingDemoUrl ? <><Loader2 size={14} className="animate-spin mr-2" /> Menyimpan...</> : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={!!previewTemplate} onOpenChange={(o) => !o && setPreviewTemplate(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layout size={20} className="text-[#2D6CDF]" />
              {previewTemplate?.name}
              <Badge className={categoryColor(previewTemplate?.category)}>{previewTemplate?.category}</Badge>
              {previewTemplate?.is_custom && <Badge variant="outline">Kustom</Badge>}
            </DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-5 py-2">
              <p className="text-sm text-slate-600">{previewTemplate.description}</p>

              {/* Color Schemes */}
              {(previewTemplate.color_schemes || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Palette size={15} /> Skema Warna ({previewTemplate.color_schemes.length})
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {previewTemplate.color_schemes.map((cs, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 bg-slate-50">
                        <div className="flex gap-1">
                          {[cs.primary, cs.secondary, cs.accent].filter(Boolean).map((c, j) => (
                            <div key={j} className="w-7 h-7 rounded-md shadow-sm border border-white" style={{ background: c }} title={c} />
                          ))}
                        </div>
                        <span className="text-xs font-medium text-slate-700 truncate">{cs.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Default Modules */}
              {(previewTemplate.default_modules || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <Star size={15} /> Modul Default ({previewTemplate.default_modules.length})
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {previewTemplate.default_modules.map((m, i) => (
                      <Badge key={i} variant="outline" className="bg-[#2D6CDF]/5 text-[#2D6CDF] border-[#2D6CDF]/20 font-mono text-[11px]">
                        {m}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Default Pages */}
              {(previewTemplate.default_pages || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <FileText size={15} /> Halaman Bawaan ({previewTemplate.default_pages.length})
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                    {previewTemplate.default_pages.map((p, i) => (
                      <div key={i} className="px-2 py-1.5 rounded border border-slate-200 bg-slate-50">
                        <p className="font-medium text-slate-900">{p.title}</p>
                        <p className="text-[10px] text-slate-500 font-mono">/{p.slug} · {p.type}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Default Settings */}
              {previewTemplate.default_settings && Object.keys(previewTemplate.default_settings).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <SettingsIcon size={15} /> Pengaturan Default
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
                    {Object.entries(previewTemplate.default_settings).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between px-2 py-1 rounded border border-slate-200 bg-slate-50">
                        <span className="text-slate-700 font-mono text-[11px]">{k}</span>
                        <Badge variant="outline" className={typeof v === 'boolean' ? (v ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500') : ''}>
                          {String(v)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Layout Options */}
              {previewTemplate.layout_options && Object.keys(previewTemplate.layout_options).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-2">Opsi Layout</h4>
                  <div className="space-y-1.5 text-xs">
                    {Object.entries(previewTemplate.layout_options).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2">
                        <span className="text-slate-700 font-mono text-[11px] min-w-[140px]">{k}:</span>
                        <div className="flex flex-wrap gap-1">
                          {(Array.isArray(v) ? v : [v]).map((opt, i) => (
                            <Badge key={i} variant="outline" className="text-[10px]">{String(opt)}</Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewTemplate(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Edit Template Kustom' : 'Buat Template Kustom'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Section 1: Info Dasar */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">1. Informasi Dasar</h3>
                <p className="text-xs text-slate-500">Identitas template yang akan tampil di list.</p>
              </div>
              {!editTarget && templates.length > 0 && (
                <div className="p-3 rounded-lg border border-blue-200 bg-blue-50">
                  <Label className="text-xs font-semibold text-blue-900 flex items-center gap-1.5">
                    <Copy size={12} /> Salin dari template bawaan (opsional)
                  </Label>
                  <select
                    onChange={(e) => cloneFromBuiltIn(e.target.value)}
                    className="mt-1.5 w-full border border-blue-200 rounded-md px-3 py-2 text-sm bg-white"
                    defaultValue=""
                  >
                    <option value="">— Mulai dari kosong —</option>
                    {templates.map((t) => (
                      <option key={t.template_id || t.id} value={t.template_id || t.id}>
                        {t.name} ({(t.default_modules || []).length} modul, {(t.default_pages || []).length} halaman)
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-blue-700 mt-1.5">Pilih untuk auto-fill modul, halaman, & skema warna. Kemudian sesuaikan.</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Nama Template *</Label>
                  <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Contoh: Toko Fashion Premium" data-testid="template-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Kategori</Label>
                  <select
                    value={form.category}
                    onChange={e => set('category', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="commerce">Commerce (Toko)</option>
                    <option value="education">Education (Kursus/LMS)</option>
                    <option value="services">Services (Jasa)</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Deskripsi *</Label>
                <Textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Deskripsi singkat untuk membantu tenant memilih template ini..." rows={2} />
              </div>

              {/* Cocok untuk Paket SaaS */}
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5">
                  <Package size={13} className="text-emerald-600" /> Cocok untuk Paket SaaS
                </Label>
                <p className="text-[11px] text-slate-500 -mt-1">
                  Pilih paket yang boleh memakai template ini. Modul-modul sesuai paket akan otomatis tercentang sebagai saran.
                  Kosongkan = tersedia untuk semua paket.
                </p>
                {packages.length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-2">Belum ada paket SaaS. Buat dulu di menu Paket SaaS.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5">
                    {packages.map((p) => {
                      const checked = (form.available_packages || []).includes(p.id);
                      const featureCount = (p.features || []).length;
                      return (
                        <label
                          key={p.id}
                          className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors text-xs ${checked ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePackageAvailability(p.id)}
                            className="mt-0.5 accent-emerald-600"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-slate-900 truncate">{p.name}</p>
                            <p className="text-[10px] text-slate-500">{featureCount} fitur</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                {(form.available_packages || []).length > 0 && (
                  <p className="text-[11px] text-emerald-700 mt-1">
                    <Sparkles size={11} className="inline mr-1" />
                    Template ini hanya tersedia untuk tenant pada paket yang dicentang.
                  </p>
                )}
              </div>
            </div>

            {/* Section 2: Modul */}
            <div className="space-y-3 border-t border-slate-200 pt-5">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
                  <Star size={14} /> 2. Modul Default <span className="text-xs font-normal text-slate-500">({form.default_modules.length} dipilih)</span>
                </h3>
                <p className="text-xs text-slate-500">Modul yang langsung aktif saat tenant pakai template ini.</p>
              </div>
              <div className="space-y-3">
                {MODULE_CATALOG.map((group) => (
                  <div key={group.group}>
                    <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{group.group}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {group.items.map((mod) => {
                        const checked = form.default_modules.includes(mod.code);
                        const isAuto = checked && autoSuggestedModules.includes(mod.code);
                        return (
                          <label
                            key={mod.code}
                            className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors text-xs ${checked ? 'border-[#2D6CDF] bg-[#2D6CDF]/5' : 'border-slate-200 hover:border-slate-300'}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleModule(mod.code)}
                              className="mt-0.5 accent-[#2D6CDF]"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm text-slate-900">{mod.label}</p>
                                {isAuto && (
                                  <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-emerald-50 text-emerald-700 border-emerald-200" title="Auto-disarankan dari Paket SaaS yang dipilih">
                                    auto
                                  </Badge>
                                )}
                              </div>
                              <p className="text-[10px] text-slate-400 font-mono">{mod.code}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {/* Custom module */}
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Modul Kustom</p>
                  <div className="flex items-center gap-2">
                    <Input
                      value={customModuleCode}
                      onChange={(e) => setCustomModuleCode(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomModule(); } }}
                      placeholder="Misal: my_custom_module"
                      className="font-mono text-xs"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={addCustomModule}>
                      <Plus size={13} className="mr-1" /> Tambah
                    </Button>
                  </div>
                  {form.default_modules.filter((c) => !MODULE_CATALOG.some((g) => g.items.some((i) => i.code === c))).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {form.default_modules.filter((c) => !MODULE_CATALOG.some((g) => g.items.some((i) => i.code === c))).map((c) => (
                        <Badge key={c} variant="outline" className="bg-emerald-50 text-emerald-700 font-mono text-[11px] gap-1">
                          {c}
                          <button type="button" onClick={() => toggleModule(c)} aria-label="Hapus modul"><X size={10} /></button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section 3: Halaman */}
            <div className="space-y-3 border-t border-slate-200 pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
                    <FileText size={14} /> 3. Halaman Bawaan <span className="text-xs font-normal text-slate-500">({form.default_pages.length})</span>
                  </h3>
                  <p className="text-xs text-slate-500">Halaman yang otomatis dibuat saat tenant memilih template ini.</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addPage}><Plus size={13} className="mr-1" /> Tambah Halaman</Button>
              </div>
              <div className="space-y-2">
                {form.default_pages.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input
                      value={p.slug}
                      onChange={(e) => updatePage(i, 'slug', e.target.value)}
                      placeholder="slug (mis. about)"
                      className="col-span-3 font-mono text-xs"
                    />
                    <Input
                      value={p.title}
                      onChange={(e) => updatePage(i, 'title', e.target.value)}
                      placeholder="Judul (mis. Tentang Kami)"
                      className="col-span-5"
                    />
                    <select
                      value={p.type}
                      onChange={(e) => updatePage(i, 'type', e.target.value)}
                      className="col-span-3 flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-xs"
                    >
                      {PAGE_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
                    </select>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removePage(i)} className="col-span-1 text-red-500 hover:bg-red-50">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
                {form.default_pages.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-4">Belum ada halaman. Klik "Tambah Halaman" untuk mulai.</p>
                )}
              </div>
            </div>

            {/* Section 4: Color Schemes */}
            <div className="space-y-3 border-t border-slate-200 pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
                    <Palette size={14} /> 4. Skema Warna <span className="text-xs font-normal text-slate-500">({form.color_schemes.length})</span>
                  </h3>
                  <p className="text-xs text-slate-500">Tenant dapat memilih salah satu palette saat onboarding. Minimal 1.</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addColorScheme}><Plus size={13} className="mr-1" /> Tambah Palette</Button>
              </div>
              <div className="space-y-3">
                {form.color_schemes.map((cs, i) => (
                  <div key={i} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <Input
                        value={cs.name}
                        onChange={(e) => updateColorScheme(i, 'name', e.target.value)}
                        placeholder="Nama palette (mis. Modern Blue)"
                        className="flex-1 bg-white"
                      />
                      {form.color_schemes.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeColorScheme(i)} className="text-red-500 hover:bg-red-50">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { key: 'primary', label: 'Primary' },
                        { key: 'secondary', label: 'Secondary' },
                        { key: 'accent', label: 'Accent' },
                      ].map((field) => (
                        <div key={field.key}>
                          <Label className="text-[10px] text-slate-500">{field.label}</Label>
                          <div className="flex items-center gap-1.5 mt-1">
                            <input
                              type="color"
                              value={cs[field.key] || '#000000'}
                              onChange={(e) => updateColorScheme(i, field.key, e.target.value)}
                              className="h-8 w-10 rounded border border-slate-200 cursor-pointer flex-shrink-0"
                            />
                            <Input
                              value={cs[field.key] || ''}
                              onChange={(e) => updateColorScheme(i, field.key, e.target.value)}
                              className="font-mono text-[11px] bg-white h-8"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {PRESET_COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => updateColorScheme(i, 'primary', c)}
                          className="w-5 h-5 rounded border border-white shadow-sm hover:scale-110 transition-transform"
                          style={{ background: c }}
                          title={`Set primary ${c}`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Batal</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90" data-testid="save-template-btn">
              {saving ? 'Menyimpan...' : editTarget ? 'Simpan Perubahan' : 'Buat Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
